/**
 * @typedef {Object} OutputField
 * @property {string} name - Field identifier
 * @property {string|function} label - Display label or function returning label
 * @property {function} [checked] - Function determining if field is selected by default
 * @property {function} [visible] - Function determining if field should be shown
 * @property {boolean} [forceChecked] - If true, field is always selected and cannot be unchecked
 * @property {function} [disabled] - Function determining if fields should be disabled
 */

import { codeTypeOutputLabelMap } from "components/QueryDetails/reports/ClinicalVolumes";

const clinicalVolumesOutputFields = [
  {
    name: "npi",
    label: ({ common }) =>
      common?.providerType === "physician"
        ? "Physician NPI"
        : "Organization NPI",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) => !!common?.outputs?.npi || !!common?.canBeVisualized
  },
  {
    name: "name",
    label: ({ common }) =>
      common?.providerType === "physician"
        ? "Physician Name"
        : "Organization Name",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.name || !!common?.canBeVisualized
  },
  {
    name: "classification",
    label: ({ common }) =>
      common?.providerType === "physician"
        ? "Physician Classification"
        : "Organization Classification",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.classification || !!common?.canBeVisualized
  },
  {
    name: "specialization",
    label: ({ common }) =>
      common?.providerType === "physician"
        ? "Physician Specialization"
        : "Organization Specialization",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.specialization || !!common?.canBeVisualized
  },

  {
    name: "address",
    label: "Address",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.address || !!common?.canBeVisualized
  },
  {
    name: "city",
    label: "City",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.city || !!common?.canBeVisualized
  },
  {
    name: "stateName",
    label: "State",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.stateName || !!common?.canBeVisualized
  },
  {
    name: "zipCode",
    label: "ZIP Code",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.zipCode || !!common?.canBeVisualized
  },

  {
    name: "outputCode",
    label: ({ common }) =>
      (typeof codeTypeOutputLabelMap !== "undefined" &&
        codeTypeOutputLabelMap?.[common?.codeType]) ||
      "Code Type",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.outputCode || !!common?.canBeVisualized
  },

  {
    name: "codeServiceLine",
    label: "Code Service Line",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.codeServiceLine || !!common?.canBeVisualized
  },
  {
    name: "codeDescription",
    label: "Code Description",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.codeDescription || !!common?.canBeVisualized
  },

  {
    name: "distinctClaimsCount",
    label: "Count of Distinct Claims",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.distinctClaimsCount || !!common?.canBeVisualized
  },
  {
    name: "distinctPatientsCount",
    label: "Count of Distinct Patients",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.distinctPatientsCount || !!common?.canBeVisualized
  },
  {
    name: "sumCharges",
    label: "Sum of Charges",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.sumCharges || !!common?.canBeVisualized
  },

  {
    name: "hospitalAffiliationIds",
    label: "Hospital Affiliation Ids",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.hospitalAffiliationIds || !!common?.canBeVisualized
  },
  {
    name: "hospitalAffiliationNames",
    label: "Hospital Affiliation Names",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.hospitalAffiliationNames || !!common?.canBeVisualized
  },
  {
    name: "groupPracticeIds",
    label: "Group Practice Ids",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.groupPracticeIds || !!common?.canBeVisualized
  },
  {
    name: "groupPracticeNames",
    label: "Group Practice Names",
    visible: ({ common }) => common?.providerType === "physician",
    disabled: ({ common }) => !!common?.canBeVisualized,
    checked: ({ common }) =>
      !!common?.outputs?.groupPracticeNames || !!common?.canBeVisualized
  }
];

export default clinicalVolumesOutputFields;
